import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Eye } from "lucide-react";
import { getS } from "../../core/state";
import { withUploadedImageCacheBust } from "../../core/imageMedia";
import { formatSkuBadgeText, formatVendorBadgeLabel, resolveVendorKeyForBadge } from "../../core/vendorDisplay";

const normalizeUrls = (value) => {
  if (Array.isArray(value)) return value.map((v) => String(v || "").trim()).filter(Boolean);
  if (typeof value === "string" && value.trim()) {
    const raw = value.trim();
    if (raw.startsWith("[") && raw.endsWith("]")) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.map((v) => String(v || "").trim()).filter(Boolean);
      } catch (e) {
        // ignore parse and continue fallback
      }
    }
    if (raw.includes(",")) return raw.split(",").map((v) => v.trim()).filter(Boolean);
    return [raw];
  }
  return [];
};

const pickProductImage = (item) => {
  const direct = [
    ...normalizeUrls(item?.images2),
    ...normalizeUrls(item?.images),
    ...normalizeUrls(item?.image),
    ...normalizeUrls(item?.thumbnail),
    ...normalizeUrls(item?.thumbnailUrl),
    ...normalizeUrls(item?.mainImage),
  ];
  const first = direct[0] || "";
  return withUploadedImageCacheBust(first, item);
};

const toLower = (v) => String(v || "").trim().toLowerCase();
const toMoney = (v) => Number(v || 0);
const toRatingCount = (item) => {
  const n = Number(item?.ratingCount ?? item?.ratingsCount ?? item?.reviewCount ?? item?.reviews ?? item?.views);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : 0;
};
const toRatingValue = (item) => {
  const n = Number(item?.rating);
  if (Number.isFinite(n) && n > 0) return Math.max(1, Math.min(5, n));
  return 4.2;
};
const toStars = (rating) => {
  const full = Math.max(1, Math.min(5, Math.round(Number(rating) || 0)));
  return "★★★★★".slice(0, full);
};

function LandingRow({ title, items, rowClickTo }) {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [nav, setNav] = useState({ show: false, canPrev: false, canNext: false });

  const updateNav = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      setNav({ show: false, canPrev: false, canNext: false });
      return;
    }
    const max = Math.max(0, el.scrollWidth - el.clientWidth);
    const left = el.scrollLeft;
    const show = max > 2;
    setNav({ show, canPrev: show && left > 1, canNext: show && left < max - 1 });
  }, []);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    const refresh = () => updateNav();
    refresh();
    const t0 = window.setTimeout(refresh, 0);
    const t1 = window.setTimeout(refresh, 280);
    const raf = window.requestAnimationFrame(refresh);
    let ro;
    if (typeof ResizeObserver !== "undefined" && el) {
      ro = new ResizeObserver(() => refresh());
      ro.observe(el);
    }
    window.addEventListener("resize", refresh);
    return () => {
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", refresh);
      ro?.disconnect();
    };
  }, [items.length, updateNav]);

  const scrollByDelta = (delta) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: delta, behavior: "smooth" });
    window.setTimeout(updateNav, 220);
    window.setTimeout(updateNav, 520);
  };

  const scrollByPage = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const w = el.clientWidth || 300;
    const delta = Math.min(340, Math.max(160, Math.floor(w * 0.82))) * direction;
    scrollByDelta(delta);
  };

  const openItem = (item) => {
    const routeId = String(item?.id ?? item?.sku ?? "").trim();
    if (!routeId) return;
    const to = String(rowClickTo || "/productDetail/:id").replace(":id", routeId);
    navigate(to, { state: { detailItem: item } });
  };

  return (
    <section className="lfx-landing-row">
      {title ? (
        <div className="lfx-landing-row-head">
          <h3>{title}</h3>
        </div>
      ) : null}
      <div className="lfx-landing-strip-wrap">
        {nav.show && nav.canPrev ? (
          <button
            type="button"
            className="lfx-landing-strip-nav lfx-landing-strip-nav--prev"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              scrollByPage(-1);
            }}
            aria-label={`Previous ${title}`}
          >
            <ChevronLeft size={16} />
          </button>
        ) : null}
        <div className="lfx-landing-strip" ref={scrollRef} onScroll={updateNav}>
          {items.map((item, idx) => (
            (() => {
              const price = toMoney(item?.price);
              const sale = toMoney(item?.discountPrice || item?.price);
              const hasOffer = price > 0 && sale > 0 && sale < price;
              const percentOff = hasOffer ? Math.round(((price - sale) / price) * 100) : 0;
              const ratingValue = toRatingValue(item);
              const ratingCount = toRatingCount(item);
              const imgSrc = pickProductImage(item);
              const vendorKey = resolveVendorKeyForBadge(String(item?.vendorId || "").trim(), [imgSrc]);
              const vendorBadge = formatVendorBadgeLabel(vendorKey);
              const skuBadge = formatSkuBadgeText(item?.sku, item?.id);
              return (
                <button
                  key={`${item?.id || item?.sku || idx}`}
                  type="button"
                  className="lfx-landing-product-card"
                  onClick={() => openItem(item)}
                  title={item?.name || "Open product"}
                >
                  <div className="lfx-landing-product-image3d">
                    <img
                      src={imgSrc}
                      alt={item?.name || "product"}
                      onLoad={updateNav}
                    />
                    {(vendorBadge || skuBadge) ? (
                      <div className="lfx-landing-thumb-codes">
                        {vendorBadge ? (
                          <span
                            className="lfx-vendor-thumb-badge lfx-vendor-thumb-badge--landing"
                          >
                            {vendorBadge}
                          </span>
                        ) : null}
                        {skuBadge ? (
                          <span className="lfx-sku-thumb-badge lfx-sku-thumb-badge--landing">
                            {skuBadge}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  <div className="lfx-landing-product-meta">
                    <span className="lfx-landing-product-name">{item?.name || "Saree"}</span>
                    <span className="lfx-landing-product-rating">
                      <span className="lfx-landing-product-rating-pill">{ratingValue.toFixed(1)} ★</span>
                      <span className="lfx-landing-product-stars">{toStars(ratingValue)}</span>
                      <span>({ratingCount})</span>
                    </span>
                    <span className="lfx-landing-product-price-line">
                      {hasOffer ? <strong className="lfx-landing-off">{`-${percentOff}%`}</strong> : null}
                      <strong className="lfx-landing-product-price">₹{sale.toLocaleString("en-IN")}.00</strong>
                    </span>
                    {price > 0 ? (
                      <span className="lfx-landing-product-mrp">MRP <em>₹{price.toLocaleString("en-IN")}.00</em></span>
                    ) : null}
                  </div>
                </button>
              );
            })()
          ))}
        </div>
        {nav.show && nav.canNext ? (
          <button
            type="button"
            className="lfx-landing-strip-nav lfx-landing-strip-nav--next"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              scrollByPage(1);
            }}
            aria-label={`Next ${title}`}
          >
            <ChevronRight size={16} />
          </button>
        ) : null}
      </div>
    </section>
  );
}

export default function LandingSection({ section }) {
  const s = getS();
  const all = Array.isArray(s?.list) ? s.list : [];
  const maxPerRow = Math.max(1, Number(section?.maxPerRow) || 12);
  const rowClickTo = section?.rowClick?.to || "/productDetail/:id";
  const rowDefs = Array.isArray(section?.rows) ? section.rows : [];

  const rows = useMemo(() => {
    return rowDefs.map((row) => {
      const title = String(row?.title || row?.category || "Category").trim();
      const categoryKey = toLower(row?.category);
      const source = categoryKey
        ? all.filter((item) => toLower(item?.category).includes(categoryKey))
        : all;
      return { title, items: source.slice(0, maxPerRow) };
    }).filter((r) => r.items.length > 0);
  }, [rowDefs, all, maxPerRow]);

  const panelMode = String(section?.panel?.mode || "recentlyAdded").trim();
  const panelTitle = String(section?.panel?.title || "What people are looking / recently added").trim();
  const panelMax = Math.max(1, Number(section?.panel?.max) || 8);
  const panelItems = useMemo(() => {
    const clone = [...all];
    if (panelMode === "mostViewed") {
      clone.sort((a, b) => Number(b?.views || 0) - Number(a?.views || 0));
    } else {
      clone.sort((a, b) => new Date(b?.uploadedAt || b?.updatedAt || 0).getTime() - new Date(a?.uploadedAt || a?.updatedAt || 0).getTime());
    }
    return clone.slice(0, panelMax);
  }, [all, panelMode, panelMax]);

  return (
    <div className="lfx-landing">
      <section className="lfx-landing-panel">
        <div className="lfx-landing-panel-head">
          <h3>{panelTitle}</h3>
        </div>
        <LandingRow title="" items={panelItems} rowClickTo={rowClickTo} />
      </section>
      {rows.map((row) => (
        <LandingRow key={row.title} title={row.title} items={row.items} rowClickTo={rowClickTo} />
      ))}
      {!rows.length && !panelItems.length ? (
        <div className="lfx-empty-state"><span>No products available yet.</span></div>
      ) : null}
      {panelMode === "mostViewed" ? (
        <div className="lfx-landing-hint"><Eye size={14} /> Items ordered by view count</div>
      ) : null}
    </div>
  );
}
